import { useEffect } from 'react'

const isTextField = (el: Element | null): el is HTMLElement =>
  !!el &&
  (el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLElement && el.isContentEditable))

/**
 * Keeps the focused text field centred in view on touch devices while the
 * on-screen keyboard opens. Re-runs when the visual viewport resizes, because
 * the keyboard finishes animating after focus fires (and iOS Safari does not
 * resize the layout viewport at all, so it needs the explicit scroll).
 */
export function useKeepFocusedFieldVisible(containerRef?: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia('(pointer: coarse)').matches) return

    const reveal = () => {
      const el = document.activeElement
      if (!isTextField(el)) return
      if (containerRef?.current && !containerRef.current.contains(el)) return
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }

    const onFocusIn = () => reveal()
    const vv = window.visualViewport

    document.addEventListener('focusin', onFocusIn)
    vv?.addEventListener('resize', reveal)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      vv?.removeEventListener('resize', reveal)
    }
  }, [containerRef])
}
